import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { SITENAMEALIAS } from "../utils/init";

const TOUR_STEPS = [
  // Step 1 — Success Banner (Top Green Bar)
  {
    target: '[data-tour="success-banner"]',
    title: "You're ready to get paid",
    text: "Your account is fully activated. Now create your first payment and start accepting payments instantly.",
    position: "bottom",
    ctaLabel: "Next",
  },
  // Step 2 — Progress Tracker
  {
    target: '[data-tour="progress-tracker"]',
    title: "You're one step away",
    text: "Setup is complete. Now let's create your first payment and start receiving funds.",
    position: "bottom",
    ctaLabel: "Next",
  },
  // Step 3 — Main Header
  {
    target: '[data-tour="main-header"]',
    title: "Get paid in minutes",
    text: "Create a payment link, share it with your customer, and receive payments instantly.",
    position: "bottom",
    ctaLabel: "Next",
  },
  // Step 4 — Primary CTA (Create Payment button)
  {
    target: '[data-tour="create-payment-btn"]',
    title: "Create your first payment",
    text: "This is the fastest way to start. No coding required—just create a link and share it.",
    position: "bottom",
    ctaLabel: "Next",
    actionPath: "/create-payment",
  },
  // Step 5 — Quick Payment button
  {
    target: '[data-tour="quick-payment-btn"]',
    title: "Instant payment option",
    text: "Use this if you already know the amount and want to generate a payment instantly.",
    position: "bottom",
    ctaLabel: "Next",
  },
  // Step 6 — Setup Box (Create your first payment link)
  {
    target: '[data-tour="setup-payment-link"]',
    title: "Your next step",
    text: "Complete this step to start receiving payments. Once done, you can track everything in your dashboard.",
    position: "top",
    ctaLabel: "Next",
    actionPath: "/create-payment",
  },
  // Step 7 — Create Dropdown (Top Right "+ Create")
  {
    target: '[data-tour="create-button"]',
    title: "Multiple ways to get paid",
    text: "Create structured payments or quick payment links depending on your use case.",
    position: "bottom",
    ctaLabel: "Next",
  },
  // Get Paid (parent)
  {
    target: '[data-tour="menu-get-paid"]',
    title: "All payment tools in one place",
    text: "Access payment links, products, and everything related to collecting payments.",
    position: "right",
    ctaLabel: "Next",
  },
  // Get Paid > Create Payment
  {
    target: '[data-tour="submenu-create-payment"]',
    title: "Create a payment link",
    text: "Generate a payment link in seconds. Share it with your customer and start receiving payments instantly.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "get-paid" },
  },
  // Get Paid > Products
  {
    target: '[data-tour="submenu-products"]',
    title: "Set up products & pricing",
    text: "Create products or plans for recurring or structured payments. Ideal for subscriptions and repeat billing.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "get-paid" },
  },
  // Transactions
  {
    target: '[data-tour="menu-transactions"]',
    title: "Track every payment",
    text: "View all incoming payments, statuses, and customer activity in real time.",
    position: "right",
    ctaLabel: "Next",
  },
  // Settlement (parent)
  {
    target: '[data-tour="menu-settlement"]',
    title: "Access and manage your funds",
    text: "This is where you control how your payments are stored, managed, and withdrawn.",
    position: "right",
    ctaLabel: "Next",
  },
  // Settlement > Settlement Wallets
  {
    target: '[data-tour="submenu-settlement-wallets"]',
    title: "View your balances",
    text: "See all your available funds, including settled and withdrawable balances across wallets.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "settlement" },
  },
  // Settlement > Add Crypto Address
  {
    target: '[data-tour="submenu-add-crypto-address"]',
    title: "Withdraw to crypto wallets",
    text: "Add your crypto wallet addresses to receive payouts directly in supported cryptocurrencies.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "settlement" },
  },
  // Settlement > Add Bank
  {
    target: '[data-tour="submenu-add-bank-account"]',
    title: "Withdraw to your bank account",
    text: "Connect your bank account to transfer funds securely from your balance.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "settlement" },
  },
  // Analytics
  {
    target: '[data-tour="menu-analytics"]',
    title: "Understand your business",
    text: "Get insights into revenue, payment success rates, and performance.",
    position: "right",
    ctaLabel: "Next",
  },
  // Team (parent)
  {
    target: '[data-tour="menu-team"]',
    title: "Manage your team",
    text: "Add team members and control who can access payments, reports, and settings.",
    position: "right",
    ctaLabel: "Next",
  },
  // Team > User Management
  {
    target: '[data-tour="submenu-user-management"]',
    title: "Control access and permissions",
    text: "Invite team members, assign roles, and manage permissions to keep your operations secure.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "team" },
  },
  // Configuration (parent)
  {
    target: '[data-tour="menu-configuration"]',
    title: "Customize your payment system",
    text: "Configure how payments work—from checkout experience to fees, limits, and customer data.",
    position: "right",
    ctaLabel: "Next",
  },
  // Configuration > Payment Setup
  {
    target: '[data-tour="submenu-payment-setup"]',
    title: "Set up how you accept payments",
    text: "Define payment methods, checkout behavior, and processing rules for your business.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "configuration" },
  },
  // Configuration > Checkout Templates
  {
    target: '[data-tour="submenu-checkout-templates"]',
    title: "Customize your checkout experience",
    text: "Design how your payment page looks and behaves to match your brand and improve conversions.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "configuration" },
  },
  // Checkout Templates > Payment Options
  {
    target: '[data-tour="submenu-payment-options"]',
    title: "Choose how customers pay",
    text: "Enable or disable payment methods and control which options are shown at checkout.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "configuration", subSection: "checkout-templates" },
  },
  // Checkout Templates > Fee Handling
  {
    target: '[data-tour="submenu-fee-handling"]',
    title: "Control who pays the fees",
    text: "Decide whether you or your customer covers transaction fees, and configure how they are applied.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "configuration", subSection: "checkout-templates" },
  },
  // Checkout Templates > Buyer Info
  {
    target: '[data-tour="submenu-buyer-info"]',
    title: "Collect customer details",
    text: "Customize what information you collect from customers during checkout.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "configuration", subSection: "checkout-templates" },
  },
  // Checkout Templates > Shipping
  {
    target: '[data-tour="submenu-shipping"]',
    title: "Add shipping rules",
    text: "Configure shipping charges and options if you sell physical products.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "configuration", subSection: "checkout-templates" },
  },
  // Checkout Templates > Discounts
  {
    target: '[data-tour="submenu-discounts"]',
    title: "Offer promotions",
    text: "Create discounts and apply them to payments to increase conversions.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "configuration", subSection: "checkout-templates" },
  },

  // Checkout Templates > Redirects
  {
    target: '[data-tour="submenu-redirects"]',
    title: "Control post-payment flow",
    text: "Redirect customers to a success page or your website after payment completion.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "configuration", subSection: "checkout-templates" },
  },
  // Checkout Templates > Rewards
  {
    target: '[data-tour="submenu-rewards"]',
    title: "Incentivize your customers",
    text: "Offer cashback or rewards to improve retention and repeat purchases.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "configuration", subSection: "checkout-templates" },
  },
  // Configuration > Limits
  {
    target: '[data-tour="submenu-limits"]',
    title: "Set payment boundaries",
    text: "Define minimum and maximum transaction limits to manage risk and compliance.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "configuration" },
  },
  // Configuration > Payment Tolerance
  {
    target: '[data-tour="submenu-payment-tolerance"]',
    title: "Handle payment variations",
    text: "Allow small differences in payment amounts—useful for crypto or fluctuating exchange rates.",
    position: "right",
    ctaLabel: "Next",
    expand: { section: "configuration" },
  },
  // Developers (parent)
  {
    target: '[data-tour="menu-developers"]',
    title: "Integrate payments into your platform",
    text: "Use APIs, keys, and webhooks to automate payments, build custom flows, and scale your business.",
    position: "top",
    ctaLabel: "Next",
  },
  // Developers > API Keys
  {
    target: '[data-tour="submenu-api-keys"]',
    title: "Secure your API access",
    text: "Manage your public (pk) and secret (sk) keys.\nUse pk on the frontend and sk only on your backend to securely interact with our APIs.",
    position: "top",
    ctaLabel: "Next",
    expand: { section: "developers" },
  },
  // Developers > API
  {
    target: '[data-tour="submenu-api"]',
    title: "Build custom payment flows",
    text: "Use our APIs to create payments, manage transactions, and control your payment logic programmatically.",
    position: "top",
    ctaLabel: "Next",
    expand: { section: "developers" },
  },
  // Developers > Domain Whitelisting
  {
    target: '[data-tour="submenu-domain-whitelisting"]',
    title: "Secure frontend API calls",
    text: "Allow only trusted domains to use your public key (pk).\nWhen your frontend sends pk + domain, our backend verifies both before issuing a short-lived client secret.",
    position: "top",
    ctaLabel: "Next",
    expand: { section: "developers" },
  },
  // Developers > Domain Whitelisting (flow detail)
  {
    target: '[data-tour="submenu-domain-whitelisting"]',
    title: "How authentication works",
    text: "Your UI sends pk + domain\nBackend verifies the domain is whitelisted\nA client_secret (valid for 15 minutes) is issued\nUI uses client_secret + pk to securely call APIs",
    position: "top",
    ctaLabel: "Next",
    expand: { section: "developers" },
  },
  // Developers > Domain Whitelisting (security positioning)
  {
    target: '[data-tour="submenu-domain-whitelisting"]',
    title: "Built for secure frontend usage",
    text: "This ensures only approved domains can initiate API calls, while keeping sensitive operations protected on the backend.",
    position: "top",
    ctaLabel: "Next",
    expand: { section: "developers" },
  },
  // Developers > Webhooks
  {
    target: '[data-tour="submenu-webhooks"]',
    title: "Automate with real-time events",
    text: "Receive instant notifications for payment events like success, failure, or settlement to automate your workflows.",
    position: "top",
    ctaLabel: "Finish",
    expand: { section: "developers" },
  },
  // Final Step — Developer CTA (Center Modal) - This will be conditionally added
];

const getTourKey = () => {
  const email = localStorage.getItem(SITENAMEALIAS + "_email") || "";
  return SITENAMEALIAS + "_product_tour_completed_" + email;
};

const ProductTour = ({ isOpen, onClose, history }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [arrowStyle, setArrowStyle] = useState({});
  const [arrowDirection, setArrowDirection] = useState("top");
  const tooltipRef = useRef(null);
  const animFrameRef = useRef(null);

  // Get verification status from localStorage (same as Sidebar component)
  const accountStatusId = localStorage.getItem(
    SITENAMEALIAS + "_account_status_id"
  );
  const isVerified = accountStatusId === "6";

  // Check if user is a sub-merchant (treat as verified for tour purposes)
  const isSubMerchant =
    localStorage.getItem(SITENAMEALIAS + "_parent_merchant_id") !== "0";
  const shouldShowFinalStep = isSubMerchant ? true : isVerified;

  // Create dynamic tour steps based on verification status
  const getTourSteps = useCallback(() => {
    if (shouldShowFinalStep) {
      return [
        ...TOUR_STEPS,
        {
          target: null,
          title: "Ready to integrate?",
          text: "Use your API keys and start building your payment flow in minutes.",
          position: "center",
          ctaLabel: "View API Docs",
          actionLabel: "View API Docs",
          actionPath: "/api-key",
          secondaryLabel: "Create Payment Link",
          secondaryPath: "/create-payment",
          isFinal: true,
        },
      ];
    }
    return TOUR_STEPS;
  }, [shouldShowFinalStep]);

  const tourSteps = getTourSteps();
  const totalSteps = tourSteps.length;
  const step = tourSteps[currentStep];

  const brokerName =
    localStorage.getItem(SITENAMEALIAS + "_exchange") || "PayBito";

  const resolveText = (text) => {
    return text ? text.replace(/\{brokerName\}/g, brokerName) : "";
  };

  const ensureExpanded = (stepData) => {
    if (!stepData.expand) return false;
    let didClick = false;
    const { section, subSection } = stepData.expand;

    if (section) {
      const sectionEl = document.querySelector(
        `.sidebar-section[data-tour="menu-${section}"]`
      );
      if (sectionEl) {
        const wrapper = sectionEl.querySelector(".section-items-wrapper");
        if (wrapper && !wrapper.classList.contains("expanded")) {
          const header = sectionEl.querySelector(".section-header");
          if (header) {
            header.click();
            didClick = true;
          }
        }
      }
    }

    if (subSection) {
      const subToggle = document.querySelector(
        `[data-tour="submenu-${subSection}"]`
      );
      if (subToggle) {
        const subWrapper = subToggle.parentElement?.querySelector(
          ".subsection-items-wrapper"
        );
        if (subWrapper && !subWrapper.classList.contains("expanded")) {
          subToggle.click();
          didClick = true;
        }
      }
    }

    return didClick;
  };

  const positionTooltip = useCallback(() => {
    const stepData = tourSteps[currentStep];
    if (!stepData) return;

    if (!stepData.target || stepData.position === "center") {
      setTargetRect(null);
      setTooltipStyle({
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      });
      setArrowStyle({ display: "none" });
      return;
    }

    const didExpand = ensureExpanded(stepData);
    const measureDelay = didExpand ? 450 : 150;

    setTimeout(() => {
      const el = document.querySelector(stepData.target);
      if (!el) {
        setTargetRect(null);
        setTooltipStyle({
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        });
        setArrowStyle({ display: "none" });
        return;
      }

      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);

      const pad = 16;
      const tooltipW = 360;
      const tooltipH = 220;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      let pos = stepData.position || "bottom";
      let top, left;

      if (pos === "right") {
        top = rect.top + rect.height / 2 - tooltipH / 2;
        left = rect.right + pad;
        if (left + tooltipW > vw) {
          pos = "left";
          left = rect.left - tooltipW - pad;
        }
        if (left < 0) {
          pos = "bottom";
        }
      }

      if (pos === "left") {
        top = rect.top + rect.height / 2 - tooltipH / 2;
        left = rect.left - tooltipW - pad;
        if (left < 0) {
          pos = "right";
          left = rect.right + pad;
        }
      }

      if (pos === "bottom") {
        top = rect.bottom + pad;
        left = rect.left + rect.width / 2 - tooltipW / 2;
        if (top + tooltipH > vh) {
          pos = "top";
        }
      }

      if (pos === "top") {
        top = rect.top - tooltipH - pad;
        left = rect.left + rect.width / 2 - tooltipW / 2;
        if (top < 0) {
          pos = "bottom";
          top = rect.bottom + pad;
        }
      }

      if (pos === "bottom" || pos === "top") {
        left = rect.left + rect.width / 2 - tooltipW / 2;
        top = pos === "bottom" ? rect.bottom + pad : rect.top - tooltipH - pad;
      }

      left = Math.max(12, Math.min(left, vw - tooltipW - 12));
      top = Math.max(12, Math.min(top, vh - tooltipH - 12));

      setTooltipStyle({
        position: "fixed",
        top: `${top}px`,
        left: `${left}px`,
      });

      setArrowDirection(pos);

      const arrowPos = {};
      if (pos === "bottom") {
        arrowPos.top = "-8px";
        arrowPos.left = `${Math.min(
          Math.max(rect.left + rect.width / 2 - left, 20),
          tooltipW - 20
        )}px`;
        arrowPos.transform = "translateX(-50%)";
        arrowPos.borderLeft = "8px solid transparent";
        arrowPos.borderRight = "8px solid transparent";
        arrowPos.borderBottom = "8px solid var(--body-color-less, #1e1e2d)";
      } else if (pos === "top") {
        arrowPos.bottom = "-8px";
        arrowPos.left = `${Math.min(
          Math.max(rect.left + rect.width / 2 - left, 20),
          tooltipW - 20
        )}px`;
        arrowPos.transform = "translateX(-50%)";
        arrowPos.borderLeft = "8px solid transparent";
        arrowPos.borderRight = "8px solid transparent";
        arrowPos.borderTop = "8px solid var(--body-color-less, #1e1e2d)";
      } else if (pos === "right") {
        arrowPos.left = "-8px";
        arrowPos.top = `${Math.min(
          Math.max(rect.top + rect.height / 2 - top, 20),
          tooltipH - 20
        )}px`;
        arrowPos.transform = "translateY(-50%)";
        arrowPos.borderTop = "8px solid transparent";
        arrowPos.borderBottom = "8px solid transparent";
        arrowPos.borderRight = "8px solid var(--body-color-less, #1e1e2d)";
      } else if (pos === "left") {
        arrowPos.right = "-8px";
        arrowPos.top = `${Math.min(
          Math.max(rect.top + rect.height / 2 - top, 20),
          tooltipH - 20
        )}px`;
        arrowPos.transform = "translateY(-50%)";
        arrowPos.borderTop = "8px solid transparent";
        arrowPos.borderBottom = "8px solid transparent";
        arrowPos.borderLeft = "8px solid var(--body-color-less, #1e1e2d)";
      }
      setArrowStyle({ ...arrowPos, position: "absolute", width: 0, height: 0 });
    }, measureDelay);
  }, [currentStep, tourSteps]);

  useEffect(() => {
    if (!isOpen) return;
    positionTooltip();

    const handleResize = () => positionTooltip();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize, true);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isOpen, currentStep, positionTooltip]);

  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      handleFinish();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem(getTourKey(), "true");
    onClose();
  };

  const handleFinish = () => {
    localStorage.setItem(getTourKey(), "true");
    onClose();
  };

  const handleAction = () => {
    if (step.actionPath && history) {
      localStorage.setItem(getTourKey(), "true");
      onClose();
      history.push(step.actionPath);
    }
  };

  const handleSecondary = () => {
    if (step.secondaryPath && history) {
      localStorage.setItem(getTourKey(), "true");
      onClose();
      history.push(step.secondaryPath);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") handleSkip();
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handleBack();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, currentStep]);

  if (!isOpen) return null;

  const spotlightPad = 8;

  // Clamp spotlight so it doesn't go off-screen on any side
  const getSpotlight = (rect) => {
    if (!rect) return null;
    const left = Math.max(0, rect.left - spotlightPad);
    const top = Math.max(0, rect.top - spotlightPad);
    const right = Math.min(window.innerWidth, rect.right + spotlightPad);
    const bottom = Math.min(window.innerHeight, rect.bottom + spotlightPad);
    return { left, top, width: right - left, height: bottom - top };
  };

  const spotlight = getSpotlight(targetRect);

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        pointerEvents: "auto",
      }}
    >
      {/* Overlay */}
      <svg
        style={{
          position: "fixed",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "auto",
        }}
        onClick={handleSkip}
      >
        <defs>
          <mask id="tour-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {spotlight && (
              <rect
                x={spotlight.left}
                y={spotlight.top}
                width={spotlight.width}
                height={spotlight.height}
                rx="12"
                ry="12"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.6)"
          mask="url(#tour-spotlight-mask)"
        />
      </svg>

      {/* Spotlight ring */}
      {spotlight && (
        <div
          style={{
            position: "fixed",
            left: spotlight.left,
            top: spotlight.top,
            width: spotlight.width,
            height: spotlight.height,
            borderRadius: "12px",
            boxShadow:
              "0 0 0 3px var(--btn-color, #284ccf), 0 0 20px rgba(40,76,207,0.4)",
            pointerEvents: "none",
            transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
            zIndex: 100000,
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        style={{
          ...tooltipStyle,
          width: "360px",
          background: "var(--body-color-less, #1e1e2d)",
          borderRadius: "16px",
          boxShadow:
            "0 20px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.08)",
          padding: "28px 24px 20px",
          zIndex: 100001,
          transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          pointerEvents: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Arrow */}
        <div style={arrowStyle} />

        {/* Step indicator */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <span
            style={{
              fontSize: "11px",
              fontWeight: "700",
              color: "var(--btn-color, #284ccf)",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            Step {currentStep + 1} of {totalSteps}
          </span>
          <div
            style={{
              flex: 1,
              height: "6px",
              borderRadius: "3px",
              background: "rgba(255,255,255,0.15)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${((currentStep + 1) / totalSteps) * 100}%`,
                height: "100%",
                borderRadius: "3px",
                background:
                  "linear-gradient(90deg, #10b981 0%, var(--btn-color, #284ccf) 100%)",
                transition: "width 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />
          </div>
        </div>

        {/* Icon for center steps */}
        {step.position === "center" && (
          <div
            style={{
              textAlign: "center",
              marginBottom: "16px",
            }}
          >
            <i
              className="fas fa-rocket"
              style={{
                fontSize: "36px",
                color: "#10b981",
              }}
            ></i>
          </div>
        )}

        {/* Title */}
        <h3
          style={{
            fontSize: "17px",
            fontWeight: "700",
            color: "var(--text-color, #fff)",
            marginBottom: "10px",
            lineHeight: "1.3",
            textAlign: step.position === "center" ? "center" : "left",
          }}
        >
          {resolveText(step.title)}
        </h3>

        {/* Text */}
        <p
          style={{
            fontSize: "13.5px",
            color: "var(--text-color, #fff)",
            opacity: 0.7,
            lineHeight: "1.65",
            marginBottom: "20px",
            whiteSpace: "pre-line",
            textAlign: step.position === "center" ? "center" : "left",
          }}
        >
          {resolveText(step.text)}
        </p>

        {/* Buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {/* Primary action + secondary for final step */}
          {step.isFinal ? (
            <>
              <button
                onClick={handleAction}
                style={{
                  padding: "10px 18px",
                  background:
                    "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "12.5px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  width: "100%",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                {step.actionLabel}
              </button>
              {step.secondaryLabel && (
                <button
                  onClick={handleSecondary}
                  style={{
                    padding: "10px 18px",
                    background: "transparent",
                    color: "var(--btn-color, #284ccf)",
                    border: "1.5px solid var(--btn-color, #284ccf)",
                    borderRadius: "8px",
                    fontSize: "12.5px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    width: "100%",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "color-mix(in srgb, var(--btn-color, #284ccf), transparent 90%)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  {step.secondaryLabel}
                </button>
              )}
            </>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              <button
                onClick={handleSkip}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--text-color, #fff)",
                  opacity: 0.45,
                  fontSize: "12.5px",
                  cursor: "pointer",
                  padding: "6px 12px",
                  fontWeight: "500",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.45")}
              >
                Skip Tour
              </button>

              <div style={{ display: "flex", gap: "8px" }}>
                {currentStep > 0 && (
                  <button
                    onClick={handleBack}
                    style={{
                      padding: "8px 18px",
                      background: "transparent",
                      color: "var(--btn-color, #284ccf)",
                      border: "1.5px solid var(--btn-color, #284ccf)",
                      borderRadius: "8px",
                      fontSize: "12.5px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "color-mix(in srgb, var(--btn-color, #284ccf), transparent 90%)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    Back
                  </button>
                )}

                <button
                  onClick={handleNext}
                  style={{
                    padding: "8px 22px",
                    background: "var(--btn-color, #284ccf)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "12.5px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  {step.ctaLabel || "Next"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProductTour;
